/** Resolves a public asset path (`/effects/x.mp3`) against the app base URL. */
export function publicAssetUrl(path: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "")
  return `${base}/${path.replace(/^\//, "")}`
}

type PrimedSound = { audio: HTMLAudioElement; claimed: boolean }

const primedSounds = new Map<string, PrimedSound>()

/** Unlocks `src` inside a user gesture, so a `playEffectSound` after async work still plays on iOS/Safari or a reload. */
export function primeEffectSound(src: string): void {
  if (typeof Audio === "undefined" || primedSounds.has(src)) return
  const audio = new Audio(publicAssetUrl(src))
  const entry: PrimedSound = { audio, claimed: false }
  primedSounds.set(src, entry)
  audio.muted = true
  const release = () => {
    if (entry.claimed) return
    audio.pause()
    audio.muted = false
  }
  const unlocking = audio.play()
  if (unlocking) unlocking.then(release, release)
  else release()
}

/** Options of `playEffectSound`; `onStart` fires once when the sound is heard, or at once if it is blocked or late. */
export type EffectSoundOptions = { volume?: number; onStart?: () => void; startTimeoutMs?: number }

/** Plays a one-shot sound effect at a gentle volume, reusing a primed element; a blocked autoplay is silent. Returns a stop function. */
export function playEffectSound(
  src: string,
  { volume = 0.3, onStart, startTimeoutMs = 1200 }: EffectSoundOptions = {},
): () => void {
  let started = false
  const start = () => {
    if (started) return
    started = true
    clearTimeout(timer)
    onStart?.()
  }
  const timer = setTimeout(start, startTimeoutMs)
  if (typeof Audio === "undefined") {
    start()
    return () => {}
  }
  const primed = primedSounds.get(src)
  if (primed) primed.claimed = true
  const audio = primed?.audio ?? new Audio(publicAssetUrl(src))
  audio.muted = false
  audio.volume = volume
  audio.currentTime = 0
  audio.addEventListener("playing", start)
  audio.play()?.catch(start)
  return () => {
    clearTimeout(timer)
    audio.removeEventListener("playing", start)
    audio.pause()
  }
}
